/**
 * Q16.16 fixed-point arithmetic — the determinism substrate (KTD1).
 *
 * One grid cell is 1.0, that is 65536 raw sub-units. Cell lookup is `v >> 16`.
 * A 128-cell map spans 8,388,608 raw units against an Int32 ceiling of
 * 2,147,483,647, so world coordinates have three orders of magnitude of headroom.
 *
 * Three properties this module guarantees, in order of importance:
 *
 *  1. **Every operation is exact and identical on every engine.** No result here
 *     depends on an implementation-approximated function. Where float64 is used
 *     internally it is used only on operands whose exact product is below 2^53,
 *     where IEEE-754 `+ - * / sqrt` are specified exactly.
 *  2. **Rounding truncates toward negative infinity**, uniformly, in every
 *     operation. This is fixed before any position is stored because the choice
 *     changes every hash thereafter.
 *  3. **Arithmetic saturates rather than wrapping.** `Math.imul` wraps silently,
 *     and an unspecified overflow inside the determinism substrate is a
 *     candidate desync rather than merely a wrong number. In debug mode a
 *     saturation trips an assertion.
 *
 * Angles are BAM16: a plain integer where 65536 is one full turn, so angle
 * arithmetic wraps for free with `& 0xffff`. Trigonometry comes from tables
 * generated at module load with BigInt integer arithmetic — never from
 * `Math.sin`, whose results legitimately differ between engines.
 */

declare const FixedBrand: unique symbol

/**
 * A Q16.16 fixed-point value. Only this module's constructors produce one, so a
 * raw float reaching a position or speed field is a compile error rather than a
 * silent desync.
 */
export type Fixed = number & { readonly [FixedBrand]: true }

/** A BAM16 angle: 65536 = one full turn. Wraps with `& 0xffff`. */
export type Angle = number

export const FRAC_BITS = 16
/** 1.0 in Q16.16 — also the size of one grid cell in raw sub-units. */
export const ONE = 65536 as Fixed
export const HALF = 32768 as Fixed
export const ZERO = 0 as Fixed
export const FIXED_MAX = 2147483647 as Fixed
export const FIXED_MIN = -2147483648 as Fixed

/** BAM16 angle constants. */
export const TURN = 65536
export const QUARTER_TURN = 16384
export const HALF_TURN = 32768

const INT32_MAX = 2147483647
const INT32_MIN = -2147483648

// ---------------------------------------------------------------------------
// Saturation
// ---------------------------------------------------------------------------

let debugChecks = false
let saturationCount = 0

/** Enable the saturation assertion. Off by default; on in tests. */
export function setDebugChecks(on: boolean): void {
  debugChecks = on
}

export function saturations(): number {
  return saturationCount
}

export function resetSaturations(): void {
  saturationCount = 0
}

export class SaturationError extends Error {
  constructor(value: number) {
    super(`fixed-point overflow: ${value} is outside [${INT32_MIN}, ${INT32_MAX}]`)
    this.name = 'SaturationError'
  }
}

/** Clamp an exact integer into the Int32 range, counting and optionally trapping. */
function sat(v: number): Fixed {
  if (v > INT32_MAX) {
    saturationCount++
    if (debugChecks) throw new SaturationError(v)
    return FIXED_MAX
  }
  if (v < INT32_MIN) {
    saturationCount++
    if (debugChecks) throw new SaturationError(v)
    return FIXED_MIN
  }
  return v as Fixed
}

// ---------------------------------------------------------------------------
// Construction and conversion
// ---------------------------------------------------------------------------

/** Reinterpret a raw Int32 (e.g. read back out of an Int32Array) as Fixed. */
export function raw(v: number): Fixed {
  return (v | 0) as Fixed
}

/** Whole units to Q16.16. `fromInt(3)` is three grid cells. */
export function fromInt(n: number): Fixed {
  return sat(Math.trunc(n) * 65536)
}

/**
 * Author-time conversion from a decimal literal. Deterministic — `*` and
 * `Math.round` are both exactly specified — but reserve it for data records and
 * constants, never for values derived from simulation state.
 */
export function fromFloat(f: number): Fixed {
  return sat(Math.round(f * 65536))
}

/** Exact rational construction: `fromRatio(1, 3)` is one third, truncated. */
export function fromRatio(num: number, den: number): Fixed {
  return div(fromInt(num), fromInt(den))
}

/** Truncate toward negative infinity to a whole unit. This is the cell index. */
export function toInt(v: Fixed): number {
  return v >> 16
}

/** Round-half-up to a whole unit. */
export function roundToInt(v: Fixed): number {
  return (v + 32768) >> 16
}

/** Render-side only: Q16.16 back to a float for interpolation and drawing. */
export function toFloat(v: Fixed): number {
  return v / 65536
}

/** The fractional part, always in [0, 1). */
export function frac(v: Fixed): Fixed {
  return (v & 0xffff) as Fixed
}

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

export function add(a: Fixed, b: Fixed): Fixed {
  return sat(a + b)
}

export function sub(a: Fixed, b: Fixed): Fixed {
  return sat(a - b)
}

export function neg(a: Fixed): Fixed {
  return sat(-a)
}

export function abs(a: Fixed): Fixed {
  return a < 0 ? sat(-a) : a
}

export function min(a: Fixed, b: Fixed): Fixed {
  return a < b ? a : b
}

export function max(a: Fixed, b: Fixed): Fixed {
  return a > b ? a : b
}

export function clamp(v: Fixed, lo: Fixed, hi: Fixed): Fixed {
  return v < lo ? lo : v > hi ? hi : v
}

export function sign(a: Fixed): number {
  return a < 0 ? -1 : a > 0 ? 1 : 0
}

/**
 * Exact Q16.16 multiply, truncating toward negative infinity.
 *
 * Splitting each operand into 16-bit halves keeps every partial product below
 * 2^47, so the float64 sum is exact — the full 62-bit product never has to
 * exist. `a = ah*65536 + al` holds for negative `a` too, because `>>` floors
 * and `& 0xffff` yields a non-negative remainder.
 */
export function mul(a: Fixed, b: Fixed): Fixed {
  const ah = a >> 16
  const al = a & 0xffff
  const bh = b >> 16
  const bl = b & 0xffff
  // floor(a*b / 65536) = ah*bh*65536 + ah*bl + al*bh + floor(al*bl / 65536),
  // exact because al*bl >= 0 is the only term with a fractional contribution.
  const p = ah * bh * 65536 + ah * bl + al * bh + ((al * bl) >>> 16)
  return sat(p)
}

/**
 * Exact Q16.16 divide, truncating toward negative infinity.
 *
 * Division by zero returns a saturated sentinel rather than throwing or
 * producing NaN — a NaN inside the simulation would poison the state hash and
 * every value downstream of it.
 */
export function div(a: Fixed, b: Fixed): Fixed {
  if (b === 0) {
    if (debugChecks && a !== 0) throw new SaturationError(Infinity)
    return a === 0 ? ZERO : a > 0 ? FIXED_MAX : FIXED_MIN
  }

  const negResult = a < 0 !== b < 0
  // |a| * 65536 <= 2^47 and |b| <= 2^31, both exact in float64.
  const x = Math.abs(a) * 65536
  const y = Math.abs(b)

  // Correctly-rounded division can land on the wrong side of an integer
  // boundary by one. Q*y <= x <= 2^47 is exact, so the remainder check is too.
  let q = Math.floor(x / y)
  const r = x - q * y
  if (r < 0) q -= 1
  else if (r >= y) q += 1

  if (!negResult) return sat(q)
  // Floor semantics for a negative quotient: exact division negates cleanly,
  // an inexact one rounds away from zero.
  const rem = x - q * y
  return sat(rem === 0 ? -q : -q - 1)
}

/**
 * `a * b / c` with the intermediate product held exactly. Use this rather than
 * `div(mul(a,b), c)` where the product would saturate — build progress
 * accumulation and damage scaling both do.
 */
export function mulDiv(a: Fixed, b: Fixed, c: Fixed): Fixed {
  if (c === 0) return a === 0 || b === 0 ? ZERO : sign(a) * sign(b) > 0 ? FIXED_MAX : FIXED_MIN
  return div(mul(a, b), c)
}

/**
 * Square root of a Q16.16 value. `sqrt(4.0)` is `2.0`.
 *
 * `v * 65536 <= 2^47` is exact, `Math.sqrt` is correctly rounded per IEEE-754
 * (and so identical on every engine), and the ±1 correction makes the result
 * the exact integer square root.
 */
export function sqrt(v: Fixed): Fixed {
  if (v <= 0) return ZERO
  const x = v * 65536
  let s = Math.floor(Math.sqrt(x))
  if (s * s > x) s -= 1
  else if ((s + 1) * (s + 1) <= x) s += 1
  return sat(s)
}

/** Exact integer square root of a non-negative integer below 2^49. */
export function isqrt(x: number): number {
  if (x <= 0) return 0
  let s = Math.floor(Math.sqrt(x))
  if (s * s > x) s -= 1
  else if ((s + 1) * (s + 1) <= x) s += 1
  return s
}

/** Squared length in Q16.16. Domain-limited like `length`. */
export function lengthSq(x: Fixed, y: Fixed): Fixed {
  return sat(add(mul(x, x), mul(y, y)))
}

/**
 * Exact Euclidean length of a Q16.16 vector.
 *
 * Domain: `|x|, |y| <= 2^24`, which is twice the span of a 128-cell map, so
 * every legitimate world-space delta fits. Inside that domain `x*x + y*y <= 2^49`
 * is exact in float64 and the integer square root is exact. Outside it the
 * operands are clamped and the saturation assertion trips, because a silently
 * wrong distance is worse than a loud one.
 */
export function length(x: Fixed, y: Fixed): Fixed {
  const LIMIT = 1 << 24
  let ax = x < 0 ? -x : x
  let ay = y < 0 ? -y : y
  if (ax > LIMIT || ay > LIMIT) {
    saturationCount++
    if (debugChecks) throw new SaturationError(ax > ay ? ax : ay)
    if (ax > LIMIT) ax = LIMIT
    if (ay > LIMIT) ay = LIMIT
  }
  return sat(isqrt(ax * ax + ay * ay))
}

/** Manhattan length. Cheap, exact, and the right metric for grid heuristics. */
export function lengthManhattan(x: Fixed, y: Fixed): Fixed {
  return sat((x < 0 ? -x : x) + (y < 0 ? -y : y))
}

/** Linear interpolation, `t` in Q16.16 over [0, 1]. Clamps outside that range. */
export function lerp(a: Fixed, b: Fixed, t: Fixed): Fixed {
  const tc = t < 0 ? ZERO : t > ONE ? ONE : t
  return add(a, mul(sub(b, a), tc))
}

/** Step `v` toward `target` by at most `maxDelta`. */
export function approach(v: Fixed, target: Fixed, maxDelta: Fixed): Fixed {
  const d = sub(target, v)
  if (d > maxDelta) return add(v, maxDelta)
  if (d < -maxDelta) return sub(v, maxDelta)
  return target
}

// ---------------------------------------------------------------------------
// Trigonometry: tables generated at load with exact integer arithmetic
// ---------------------------------------------------------------------------

const SIN_BITS = 12
/** 4096 entries per turn, linearly interpolated over the low 4 angle bits. */
const SIN_SIZE = 1 << SIN_BITS
const SIN_SHIFT = 16 - SIN_BITS
const SIN_MASK = SIN_SIZE - 1

const ATAN_BITS = 10
/** 1024 entries over a ratio of [0, 1], stored with 8 extra fractional bits. */
const ATAN_SIZE = 1 << ATAN_BITS
const ATAN_FRAC = 8

const sinTable = new Int32Array(SIN_SIZE + 1)
const atanTable = new Int32Array(ATAN_SIZE + 1)

/**
 * Table generation runs once at module load in BigInt, so it depends on no
 * approximated function and produces byte-identical tables on every engine.
 * The alternative — seeding from `Math.sin` — would hand exactly the
 * cross-engine variance we are avoiding straight into the substrate.
 */
;(function generateTables(): void {
  const Q = 64n
  const UNIT = 1n << Q
  // π to 40 significant digits, as an exact rational scaled into Q64.
  const PI = (3141592653589793238462643383279502884197n * UNIT) / 10n ** 39n
  const TAU = PI * 2n
  const HALF_PI = PI / 2n
  const QUARTER_PI = PI / 4n

  /** sin(x) for x in Q64 radians, by Taylor series after octant reduction. */
  const sinQ = (input: bigint): bigint => {
    let x = ((input % TAU) + TAU) % TAU
    let s = 1n
    if (x > PI) {
      s = -1n
      x -= PI
    }
    if (x > HALF_PI) x = PI - x
    let term = x
    let sum = x
    const x2 = (x * x) >> Q
    for (let k = 1n; k <= 24n; k++) {
      term = ((term * x2) >> Q) / (2n * k * (2n * k + 1n))
      sum += k % 2n === 1n ? -term : term
    }
    return s * sum
  }

  /** atan(z) for z in Q64 over [0, 1], reduced then Taylor. */
  const atanQ = (input: bigint): bigint => {
    let z = input
    let base = 0n
    if (z > (UNIT * 4n) / 10n) {
      // atan(z) = π/4 + atan((z-1)/(z+1)); brings |z| under 0.43 so the
      // alternating series converges in tens of terms rather than millions.
      base = QUARTER_PI
      z = ((z - UNIT) << Q) / (z + UNIT)
    }
    let term = z
    let sum = z
    const z2 = (z * z) >> Q
    for (let k = 1n; k <= 60n; k++) {
      term = (term * z2) >> Q
      const t = term / (2n * k + 1n)
      sum += k % 2n === 1n ? -t : t
    }
    return base + sum
  }

  for (let i = 0; i <= SIN_SIZE; i++) {
    const x = (TAU * BigInt(i)) / BigInt(SIN_SIZE)
    sinTable[i] = Number(sinQ(x) >> (Q - 16n))
  }

  // atanTable[i] = atan(i / ATAN_SIZE) in BAM16 with ATAN_FRAC extra bits.
  const bamScale = BigInt(TURN << ATAN_FRAC)
  for (let i = 0; i <= ATAN_SIZE; i++) {
    const z = (UNIT * BigInt(i)) / BigInt(ATAN_SIZE)
    atanTable[i] = Number((atanQ(z) * bamScale) / TAU)
  }
})()

/** sin of a BAM16 angle, as Q16.16 in [-1, 1]. */
export function sin(a: Angle): Fixed {
  const idx = (a >> SIN_SHIFT) & SIN_MASK
  const f = a & ((1 << SIN_SHIFT) - 1)
  const s0 = sinTable[idx]
  const s1 = sinTable[idx + 1]
  return ((s0 + (((s1 - s0) * f) >> SIN_SHIFT)) | 0) as Fixed
}

/** cos of a BAM16 angle, as Q16.16 in [-1, 1]. */
export function cos(a: Angle): Fixed {
  return sin((a + QUARTER_TURN) & 0xffff)
}

/**
 * Four-quadrant arctangent of a Q16.16 vector, as a BAM16 angle.
 * `atan2(0, 1)` is 0; angle increases counter-clockwise.
 */
export function atan2(y: Fixed, x: Fixed): Angle {
  if (x === 0 && y === 0) return 0
  const ax = (x < 0 ? -x : x) as Fixed
  const ay = (y < 0 ? -y : y) as Fixed

  let octant: number
  if (ay <= ax) {
    octant = atanLookup(div(ay, ax))
  } else {
    octant = QUARTER_TURN - atanLookup(div(ax, ay))
  }

  if (x >= 0) {
    return (y >= 0 ? octant : TURN - octant) & 0xffff
  }
  return (y >= 0 ? HALF_TURN - octant : HALF_TURN + octant) & 0xffff
}

/** atan of a Q16.16 ratio in [0, 1], as a BAM16 angle. */
function atanLookup(ratio: Fixed): number {
  const r = ratio < 0 ? 0 : ratio > ONE ? ONE : ratio
  // ratio is Q16.16 over [0, 1]; the table is indexed by its top ATAN_BITS.
  const scaled = (r * ATAN_SIZE) >> 16
  const idx = scaled >= ATAN_SIZE ? ATAN_SIZE - 1 : scaled
  const f = ((r * ATAN_SIZE) & 0xffff) >> (16 - 8)
  const a0 = atanTable[idx]
  const a1 = atanTable[idx + 1]
  return (a0 + (((a1 - a0) * f) >> 8)) >> ATAN_FRAC
}

/** Shortest signed difference between two BAM16 angles, in [-32768, 32767]. */
export function angleDiff(from: Angle, to: Angle): number {
  return (((to - from) & 0xffff) << 16) >> 16
}

/** Rotate `from` toward `to` by at most `maxStep` BAM16 units. */
export function turnToward(from: Angle, to: Angle, maxStep: number): Angle {
  const d = angleDiff(from, to)
  if (d > maxStep) return (from + maxStep) & 0xffff
  if (d < -maxStep) return (from - maxStep) & 0xffff
  return to & 0xffff
}

/** Test-only: expose the raw tables so their identities can be asserted. */
export const _tables = { sinTable, atanTable, SIN_SIZE, ATAN_SIZE }
